import { extractAppErrorEnvelope, sanitizeErrorDetail } from '../../../shared/app-errors.js';

export type FetchJsonFailurePhase = 'network' | 'response';
export type FetchJsonResponseBodyKind = 'empty' | 'html' | 'json' | 'text';

type FetchJsonErrorOptions = {
  code?: string;
  details?: string;
  diagnostic?: Record<string, unknown>;
  failurePhase?: FetchJsonFailurePhase;
  networkErrorMessage?: string;
  networkErrorName?: string;
  requestDurationMs?: number;
  requestId?: string;
  requestMethod?: string;
  requestPath?: string;
  requestQueryKeys?: string[];
  requestUrl?: string;
  responseBodyKind?: FetchJsonResponseBodyKind;
  responseBodyPreview?: string;
  responseContentType?: string | null;
  retryable?: boolean;
  cause?: unknown;
};

export class FetchJsonError extends Error {
  code?: string;
  details?: string;
  diagnostic?: Record<string, unknown>;
  failurePhase?: FetchJsonFailurePhase;
  networkErrorMessage?: string;
  networkErrorName?: string;
  requestDurationMs?: number;
  requestId?: string;
  requestMethod?: string;
  requestPath?: string;
  requestQueryKeys?: string[];
  requestUrl?: string;
  responseBodyKind?: FetchJsonResponseBodyKind;
  responseBodyPreview?: string;
  responseContentType?: string | null;
  retryable?: boolean;
  status: number;
  payload: unknown;

  constructor(message: string, status: number, payload: unknown, options?: FetchJsonErrorOptions) {
    super(message, options?.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'FetchJsonError';
    this.code = options?.code;
    this.details = options?.details;
    this.diagnostic = options?.diagnostic;
    this.failurePhase = options?.failurePhase;
    this.networkErrorMessage = options?.networkErrorMessage;
    this.networkErrorName = options?.networkErrorName;
    this.requestDurationMs = options?.requestDurationMs;
    this.requestId = options?.requestId;
    this.requestMethod = options?.requestMethod;
    this.requestPath = options?.requestPath;
    this.requestQueryKeys = options?.requestQueryKeys;
    this.requestUrl = options?.requestUrl;
    this.responseBodyKind = options?.responseBodyKind;
    this.responseBodyPreview = options?.responseBodyPreview;
    this.responseContentType = options?.responseContentType;
    this.retryable = options?.retryable;
    this.status = status;
    this.payload = payload;
  }
}

const HTML_RESPONSE_PATTERN = /^\s*<!doctype html|^\s*<html[\s>]|<head[\s>]|<body[\s>]/i;

function isHtmlLikePayload(payload: unknown): payload is string {
  return typeof payload === 'string' && HTML_RESPONSE_PATTERN.test(payload);
}

const ABSOLUTE_URL_PATTERN = /^[a-z][a-z\d+\-.]*:\/\//i;
const DEFAULT_URL_BASE = 'https://anysync.local';
const LONG_ID_SEGMENT_PATTERN = /^(?:[0-9a-f]{16,}|[a-z0-9_-]{20,})$/i;
const UUID_SEGMENT_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MAX_BODY_PREVIEW_LENGTH = 300;
let hasHandledUnauthorizedResponse = false;

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string' || input instanceof URL) {
    return input.toString();
  }

  return input.url;
}

function getRequestMethod(input: RequestInfo | URL, init: RequestInit | undefined): string {
  const method =
    init?.method ??
    (typeof Request !== 'undefined' && input instanceof Request ? input.method : undefined) ??
    'GET';
  return method.toUpperCase();
}

function getRequestSignal(input: RequestInfo | URL, init: RequestInit | undefined) {
  return (
    init?.signal ??
    (typeof Request !== 'undefined' && input instanceof Request ? input.signal : undefined)
  );
}

function redactPathSegment(segment: string): string {
  let decoded: string;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    decoded = segment;
  }

  if (UUID_SEGMENT_PATTERN.test(decoded) || LONG_ID_SEGMENT_PATTERN.test(decoded)) {
    return ':id';
  }
  return segment;
}

function redactPath(pathname: string): string {
  return pathname
    .split('/')
    .map((segment) => (segment.length > 0 ? redactPathSegment(segment) : segment))
    .join('/');
}

function resolveRequestMetadata(input: RequestInfo | URL, init: RequestInit | undefined) {
  const rawUrl = getRequestUrl(input);
  const isAbsolute = ABSOLUTE_URL_PATTERN.test(rawUrl);

  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : DEFAULT_URL_BASE;
    const parsed = new URL(rawUrl, base);
    const requestPath = redactPath(parsed.pathname);
    const requestQueryKeys = Array.from(new Set(parsed.searchParams.keys())).sort();
    const querySuffix =
      requestQueryKeys.length > 0
        ? `?${requestQueryKeys.map((key) => `${encodeURIComponent(key)}=[redacted]`).join('&')}`
        : '';

    return {
      requestMethod: getRequestMethod(input, init),
      requestPath,
      requestQueryKeys: requestQueryKeys.length > 0 ? requestQueryKeys : undefined,
      requestUrl: `${isAbsolute ? parsed.origin : ''}${requestPath}${querySuffix}`,
    };
  } catch {
    const [urlWithoutQuery] = rawUrl.split('?');
    return {
      requestMethod: getRequestMethod(input, init),
      requestUrl: rawUrl.includes('?') ? `${urlWithoutQuery}?[redacted]` : rawUrl,
    };
  }
}

function normalizeHeader(value: string | null): string | undefined {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

function normalizePreviewText(rawText: string | null): string | undefined {
  if (!rawText) {
    return undefined;
  }

  const withoutScriptOrStyle = rawText
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ');
  const plainText = isHtmlLikePayload(rawText)
    ? withoutScriptOrStyle.replace(/<[^>]+>/g, ' ')
    : rawText;
  const collapsed = plainText.replace(/\s+/g, ' ').trim();
  if (!collapsed) {
    return undefined;
  }

  const sanitized = sanitizeErrorDetail(collapsed);
  if (typeof sanitized !== 'string') {
    return undefined;
  }

  return sanitized.length > MAX_BODY_PREVIEW_LENGTH
    ? `${sanitized.slice(0, MAX_BODY_PREVIEW_LENGTH)}...`
    : sanitized;
}

function isAbortLikeError(error: unknown): boolean {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    return error.name === 'AbortError';
  }

  return error instanceof Error && error.name === 'AbortError';
}

function isTimeoutLikeError(error: unknown): boolean {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    return error.name === 'TimeoutError' || /timed?\s*out|timeout/i.test(error.message);
  }

  return error instanceof Error && /timed?\s*out|timeout/i.test(error.message);
}

function isTimeoutSignal(signal: AbortSignal | null | undefined): boolean {
  if (!signal?.aborted) {
    return false;
  }

  const reason = signal.reason;
  return isTimeoutLikeError(reason) || String(reason ?? '').match(/timed?\s*out|timeout/i) !== null;
}

function getNetworkErrorName(error: unknown): string | undefined {
  return error instanceof Error ? error.name : undefined;
}

function getNetworkErrorMessage(error: unknown): string | undefined {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  if (typeof error === 'string' && error.trim()) {
    return error;
  }

  return undefined;
}

function resolveErrorMessage(payload: unknown, fallback: string): string {
  if (isHtmlLikePayload(payload)) {
    return fallback;
  }

  if (Array.isArray(payload) && payload.length > 0) {
    const first = payload[0];
    if (first && typeof first === 'object' && 'message' in first) {
      const message = (first as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim().length > 0) {
        return message;
      }
    }
  }

  if (payload && typeof payload === 'object') {
    const obj = payload as { message?: unknown; detail?: unknown; error?: unknown };
    for (const candidate of [obj.message, obj.detail, obj.error]) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate;
      }
    }
  }

  if (typeof payload === 'string' && payload.trim().length > 0) {
    return payload;
  }

  return fallback;
}

async function readPayload(response: Response): Promise<{
  bodyKind: FetchJsonResponseBodyKind;
  payload: unknown;
  rawText: string | null;
}> {
  const text = await response.text();
  if (!text) {
    return {
      bodyKind: 'empty',
      payload: null,
      rawText: null,
    };
  }

  try {
    return {
      bodyKind: 'json',
      payload: JSON.parse(text),
      rawText: text,
    };
  } catch {
    return {
      bodyKind: isHtmlLikePayload(text) ? 'html' : 'text',
      payload: text,
      rawText: text,
    };
  }
}

export function resetFetchJsonUnauthorizedRedirectForTests() {
  hasHandledUnauthorizedResponse = false;
}

export function handleUnauthorizedFetchJsonResponse() {
  if (hasHandledUnauthorizedResponse || typeof window === 'undefined') {
    return;
  }

  hasHandledUnauthorizedResponse = true;
  window.dispatchEvent(new CustomEvent('anysync:session-expired'));

  const currentPath = `${window.location.pathname}${window.location.search}`;
  if (window.location.pathname === '/login') {
    return;
  }

  const redirect = encodeURIComponent(currentPath || '/projects');
  window.location.assign(`/login?redirect=${redirect}`);
}

export async function fetchJson<T>(
  input: RequestInfo | URL,
  init?: RequestInit,
  fallbackErrorMessage = 'Request failed',
): Promise<T> {
  const startedAt = nowMs();
  const requestMetadata = resolveRequestMetadata(input, init);
  const signal = getRequestSignal(input, init);
  let response: Response;

  try {
    response = await fetch(input, {
      credentials: 'include',
      ...init,
    });
  } catch (error) {
    const timeoutLike = isTimeoutLikeError(error) || isTimeoutSignal(signal);
    if (isAbortLikeError(error) && !timeoutLike) {
      throw error;
    }

    const networkErrorMessage = getNetworkErrorMessage(error);
    throw new FetchJsonError(
      timeoutLike ? 'Request timeout' : 'Network request failed before the server responded.',
      0,
      null,
      {
        ...requestMetadata,
        code: timeoutLike ? 'request_timeout' : 'network_request_failed',
        details: networkErrorMessage,
        failurePhase: 'network',
        networkErrorMessage,
        networkErrorName: getNetworkErrorName(error),
        requestDurationMs: Math.round(nowMs() - startedAt),
        retryable: true,
        cause: error,
      },
    );
  }

  const { bodyKind, payload, rawText } = await readPayload(response);
  const envelope = extractAppErrorEnvelope(payload);
  const requestId = envelope?.requestId ?? normalizeHeader(response.headers.get('x-request-id'));

  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorizedFetchJsonResponse();
    }

    const fallbackMessage =
      isHtmlLikePayload(payload) && response.status >= 500
        ? 'The service is temporarily unavailable. Please try again in a moment.'
        : isHtmlLikePayload(payload)
          ? 'The service returned an unexpected HTML response. Please verify the endpoint and try again.'
          : fallbackErrorMessage;

    throw new FetchJsonError(
      envelope?.message ?? resolveErrorMessage(payload, fallbackMessage),
      response.status,
      payload,
      {
        code: envelope?.code,
        details: envelope?.details,
        diagnostic: envelope?.diagnostic,
        failurePhase: 'response',
        requestDurationMs: Math.round(nowMs() - startedAt),
        requestId,
        ...requestMetadata,
        responseBodyKind: bodyKind,
        responseBodyPreview: normalizePreviewText(rawText),
        responseContentType: response.headers.get('content-type'),
        retryable: envelope?.retryable,
      },
    );
  }

  return payload as T;
}
